
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { X, Upload, FileText, AlertCircle, Save } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { ContaBancaria, LancamentoExtrato } from "@/types/financeiro";
import { useToast } from "@/components/ui/use-toast";
import { v4 as uuidv4 } from "uuid";

interface ImportacaoExtratoProps {
  onClose: () => void;
}

const ImportacaoExtrato: React.FC<ImportacaoExtratoProps> = ({ onClose }) => {
  const [contas, setContas] = useState<ContaBancaria[]>([]);
  const [contaSelecionada, setContaSelecionada] = useState<string>("");
  const [tipoImportacao, setTipoImportacao] = useState<string>("manual");
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [dataInicial, setDataInicial] = useState<string>("");
  const [dataFinal, setDataFinal] = useState<string>("");
  const [saldoInicial, setSaldoInicial] = useState<string>("");
  const { toast } = useToast();

  // Load saved accounts on component mount
  useEffect(() => {
    const savedContas = localStorage.getItem('contas_bancarias');
    if (savedContas) {
      setContas(JSON.parse(savedContas));
    }
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setArquivo(e.target.files[0]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Find the selected account
    const contaSelecionadaObj = contas.find(c => c.id === contaSelecionada);
    if (!contaSelecionadaObj) {
      toast({
        title: "Erro",
        description: "Selecione uma conta válida",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    if (tipoImportacao === 'manual') {
      // Generate mock transactions for manual import
      const saldoInicialValue = parseFloat(saldoInicial) || 0;
      
      // Generate random transactions for the period
      const initialDate = new Date(dataInicial);
      const finalDate = new Date(dataFinal);
      
      if (isNaN(initialDate.getTime()) || isNaN(finalDate.getTime())) {
        toast({
          title: "Erro",
          description: "Datas inválidas",
          variant: "destructive",
          duration: 3000,
        });
        return;
      }
      
      // Generate some random transactions
      const transacoes: LancamentoExtrato[] = [];
      let saldoAtual = saldoInicialValue;
      
      const categorias = ['Alimentação', 'Transporte', 'Salário', 'Investimento', 'Lazer', 'Saúde'];
      
      // Generate 5-10 random transactions
      const numTransactions = Math.floor(Math.random() * 5) + 5;
      
      for (let i = 0; i < numTransactions; i++) {
        const date = new Date(initialDate);
        date.setDate(initialDate.getDate() + Math.floor(Math.random() * (finalDate.getDate() - initialDate.getDate())));
        
        const tipo = Math.random() > 0.5 ? 'credito' : 'debito';
        const valor = parseFloat((Math.random() * 1000).toFixed(2));
        
        if (tipo === 'credito') {
          saldoAtual += valor;
        } else {
          saldoAtual -= valor;
        }
        
        transacoes.push({
          id: uuidv4(),
          contaBancariaId: contaSelecionada,
          data: date,
          descricao: tipo === 'credito' ? 
            'Recebimento - ' + (Math.random() > 0.5 ? 'Cliente' : 'Depósito') : 
            'Pagamento - ' + (Math.random() > 0.5 ? 'Fornecedor' : 'Débito'),
          valor,
          tipo,
          categoria: categorias[Math.floor(Math.random() * categorias.length)],
          saldoApos: parseFloat(saldoAtual.toFixed(2)),
          conciliado: false
        });
      }
      
      // Sort transactions by date
      transacoes.sort((a, b) => new Date(a.data).getTime() - new Date(b.data).getTime());
      
      // Save transactions to localStorage
      const savedTransacoes = localStorage.getItem('lancamentos_extrato');
      let allTransacoes: LancamentoExtrato[] = savedTransacoes ? JSON.parse(savedTransacoes) : [];
      
      // Remove any existing transactions for this account in this period
      allTransacoes = allTransacoes.filter(t => 
        t.contaBancariaId !== contaSelecionada || 
        new Date(t.data).getTime() < initialDate.getTime() || 
        new Date(t.data).getTime() > finalDate.getTime()
      );
      
      // Add new transactions
      allTransacoes = [...allTransacoes, ...transacoes];
      
      // Save to localStorage
      localStorage.setItem('lancamentos_extrato', JSON.stringify(allTransacoes));
      
      // Update account balance
      const updatedContas = contas.map(c => {
        if (c.id === contaSelecionada) {
          return { ...c, saldoAtual: saldoAtual };
        }
        return c;
      });
      
      localStorage.setItem('contas_bancarias', JSON.stringify(updatedContas));
      
      toast({
        title: "Extrato importado",
        description: `${transacoes.length} lançamentos foram importados com sucesso`,
        duration: 3000,
      });
    } else {
      // For file import types (OFX, CSV)
      if (!arquivo) {
        toast({
          title: "Erro",
          description: "Selecione um arquivo para importar",
          variant: "destructive",
          duration: 3000,
        });
        return;
      }
      
      toast({
        title: "Importação iniciada",
        description: `Importando arquivo ${arquivo.name}`,
        duration: 3000,
      });
      
      // In a real scenario, we would parse the file here
      // For demo purposes, show a success message after a brief delay
      setTimeout(() => {
        toast({
          title: "Extrato importado",
          description: "Os dados do arquivo foram importados com sucesso",
          duration: 3000,
        });
        onClose();
      }, 1500);
    }
    
    onClose();
  };

  return (
    <Card className="border border-financeiro-border-light">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-lg text-financeiro-text-DEFAULT">
          Importar Extrato Bancário
        </CardTitle>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="conta-select" className="text-xs">Selecione a Conta Bancária</Label>
            <Select value={contaSelecionada} onValueChange={setContaSelecionada} required>
              <SelectTrigger id="conta-select" className="text-xs">
                <SelectValue placeholder="Selecione uma conta" />
              </SelectTrigger>
              <SelectContent>
                {contas.length > 0 ? (
                  contas.map((conta) => (
                    <SelectItem key={conta.id} value={conta.id} className="text-xs">
                      {conta.descricao} - {conta.banco}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="sem-contas" disabled className="text-xs">
                    Nenhuma conta cadastrada
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {contas.length === 0 && (
              <p className="text-xs text-amber-600 flex items-center mt-1">
                <AlertCircle className="h-4 w-4 mr-1" />
                Você precisa cadastrar uma conta bancária primeiro
              </p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="tipo-importacao" className="text-xs">Tipo de Importação</Label>
            <Select value={tipoImportacao} onValueChange={setTipoImportacao}>
              <SelectTrigger id="tipo-importacao" className="text-xs">
                <SelectValue placeholder="Selecione o tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manual" className="text-xs">Entrada Manual</SelectItem>
                <SelectItem value="ofx" className="text-xs">Arquivo OFX</SelectItem>
                <SelectItem value="csv" className="text-xs">Arquivo CSV</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {tipoImportacao === 'manual' ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="data-inicial" className="text-xs">Data Inicial</Label>
                  <Input 
                    id="data-inicial" 
                    type="date" 
                    value={dataInicial}
                    className="text-xs"
                    onChange={(e) => setDataInicial(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="data-final" className="text-xs">Data Final</Label>
                  <Input 
                    id="data-final" 
                    type="date" 
                    value={dataFinal}
                    className="text-xs"
                    onChange={(e) => setDataFinal(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="saldo-inicial" className="text-xs">Saldo Inicial</Label>
                <Input 
                  id="saldo-inicial" 
                  type="number" 
                  step="0.01" 
                  placeholder="0,00"
                  className="text-xs"
                  value={saldoInicial}
                  onChange={(e) => setSaldoInicial(e.target.value)}
                  required
                />
              </div>
            </>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="arquivo" className="text-xs">Arquivo {tipoImportacao.toUpperCase()}</Label>
              <div className="border-2 border-dashed border-financeiro-border-light rounded-lg p-6 text-center">
                {arquivo ? (
                  <div className="text-financeiro-text-DEFAULT">
                    <FileText className="h-8 w-8 mx-auto mb-2" />
                    <p className="text-xs">{arquivo.name}</p>
                    <p className="text-xs text-financeiro-text-light">
                      {(arquivo.size / 1024).toFixed(2)} KB
                    </p>
                    <Button 
                      type="button"
                      variant="ghost"
                      className="mt-2 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setArquivo(null)}
                    >
                      Remover
                    </Button>
                  </div>
                ) : (
                  <>
                    <Upload className="h-10 w-10 mx-auto mb-3 text-financeiro-text-light" />
                    <p className="mb-1 text-xs text-financeiro-text-DEFAULT">
                      Arraste e solte seu arquivo aqui ou
                    </p>
                    <Button 
                      type="button"
                      variant="outline"
                      className="mt-2 text-xs"
                      onClick={() => document.getElementById('upload-file')?.click()}
                    >
                      Selecionar Arquivo
                    </Button>
                    <input
                      id="upload-file"
                      type="file"
                      accept={tipoImportacao === 'ofx' ? '.ofx' : '.csv'}
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </>
                )}
              </div>
            </div>
          )}
        </CardContent>
        
        <CardFooter className="flex justify-between border-t border-financeiro-border-light pt-4">
          <Button 
            type="button" 
            variant="outline" 
            onClick={onClose}
            className="border-financeiro-border-DEFAULT text-financeiro-text-DEFAULT text-xs"
          >
            Cancelar
          </Button>
          <Button 
            type="submit"
            className="bg-financeiro-accent-DEFAULT hover:bg-financeiro-accent-dark text-xs"
            disabled={contas.length === 0 || 
              (tipoImportacao !== 'manual' && !arquivo) || 
              (tipoImportacao === 'manual' && (!dataInicial || !dataFinal || !saldoInicial))}
          >
            <Save className="h-4 w-4 mr-1" />
            Importar Extrato
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};

export default ImportacaoExtrato;
